"use client"

import type { Zone } from "@/lib/zones"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { ArrowLeft, Flower2, Airplay as Highway, Lightbulb, Clock, Loader2, Sparkles } from "lucide-react"
import Link from "next/link"
import { useState } from "react"
import { motion } from "framer-motion"
import { useRouter } from "next/navigation"
import { savePendingEntry } from "@/lib/pending-entry"

interface ZoneDetailProps {
  zone: Zone
  onBack: () => void
  onSave: (note?: string) => void
  saving?: boolean
  isAnonymous?: boolean
}

export function ZoneDetail({ zone, onBack, onSave, saving, isAnonymous = false }: ZoneDetailProps) {
  const [note, setNote] = useState("")
  const router = useRouter()

  const handleSaveClick = () => {
    if (isAnonymous) {
      // Store pending entry and redirect to sign-up
      savePendingEntry({
        zoneId: zone.id,
        note: note || "",
        timestamp: Date.now(),
      })
      router.push("/sign-up?pendingEntry=true")
      return
    }

    // Normal save flow for authenticated users
    onSave(note || undefined)
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 sm:space-y-4">
      <Button variant="ghost" onClick={onBack} className="mb-0 min-h-[44px]">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Zpět
      </Button>

      <Card
        className="overflow-hidden"
        style={{
          borderTop: `6px solid ${zone.color}`,
        }}
      >
        <CardHeader className="text-center pb-3 sm:pb-4 px-3 sm:px-5">
          <div className="text-5xl sm:text-6xl mb-2 sm:mb-3">{zone.emoji}</div>
          <CardTitle className="text-xl sm:text-2xl text-balance">{zone.name}</CardTitle>
          <CardDescription className="text-sm sm:text-base text-pretty">{zone.description}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-5 pb-4 sm:pb-5">
          <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 sm:gap-4">
            <Card className="bg-muted/50">
              <CardHeader className="pb-0">
                <CardTitle className="text-xs sm:text-sm flex items-center gap-2 mb-2">
                  <Flower2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                  Metafora kytky
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs sm:text-sm text-pretty leading-relaxed">{zone.plantMetaphor}</p>
              </CardContent>
            </Card>

            <Card className="bg-muted/50">
              <CardHeader className="pb-0">
                <CardTitle className="text-xs sm:text-sm flex items-center gap-2 mb-2">
                  <Highway className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                  Metafora dálnice
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs sm:text-sm text-pretty leading-relaxed">{zone.highwayMetaphor}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-accent/30">
            <CardHeader className="pb-0">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-2 mb-2">
                <Lightbulb className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                Co dělat teď
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs sm:text-sm leading-relaxed text-pretty">{zone.shortTermAction}</p>
            </CardContent>
          </Card>

          <Card className="bg-accent/30">
            <CardHeader className="pb-0">
              <CardTitle className="text-xs sm:text-sm flex items-center gap-2 mb-2">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                Co dělat dlouhodobě
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs sm:text-sm leading-relaxed text-pretty">{zone.longTermAction}</p>
            </CardContent>
          </Card>
          {/* AI Reflection teaser for anonymous users */}
          {isAnonymous && (
            <Card className="border-dashed border-muted-foreground/30 bg-muted/20">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                      Zaregistruj se pro personalizované AI postřehy a reflexe k tvému aktuálnímu stavu.
                    </p>
                    <Button variant="link" className="p-0 h-auto text-xs sm:text-sm" asChild>
                      <Link href="/sign-up">Vytvořit účet</Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            <label htmlFor="note" className="text-xs sm:text-sm font-medium">
              Poznámka k dnešnímu dni (volitelné)
            </label>
            <Textarea
              id="note"
              placeholder="Jak se cítíš? Co se děje?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="resize-none text-sm"
              rows={3}
            />
          </div>

          <Button
            onClick={handleSaveClick}
            className="w-full min-h-[48px]"
            size="lg"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Ukládám...
              </>
            ) : isAnonymous ? (
              "Zaregistrovat a uložit"
            ) : (
              "Uložit dnešní stav"
            )}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  )
}
